/**
 * Pinia store for managing multi-location checkbox selection in timelines.
 *
 * Supports:
 * - Flat locations (directly in sections)
 * - Nested location hierarchies (locations containing sub-locations)
 * - Aggregate checkbox states for sections and parent locations
 * - An "initial" location that triggered selection mode (always selected, cannot be deselected)
 *
 * Components register themselves on mount and unregister on unmount.
 * Selection mode is activated by an external trigger (the allocation popup) and ends either the same way, or
 * here when the initiating location's own row unregisters.
 */

import { $t } from '@/plugins/fluent';
import { defineStore } from 'pinia';
import type { CopyTargetLocation } from './allocation/types';
import type { LocationTypeCategory } from './Timeline.types';

/**
 * Combined checkbox state returned by getters.
 * Uses discriminated union - tooltip only exists when selection mode is active.
 */
export type CheckboxState =
    | { status: 'No-Selection' }
    | { status: 'Selected'; tooltip: string }
    | { status: 'Partially-Selected'; tooltip: string }
    | { status: 'Not-Selected'; tooltip: string }
    | { status: 'Disabled-Selected'; tooltip: string }
    | { status: 'Disabled-Not-Selected'; tooltip: string };

/**
 * Extract just the status type from CheckboxState.
 */
export type SelectionStatus = CheckboxState['status'];

type LocationEntry = {
    isSelected: boolean;
    /**
     * Parent reference:
     * - sectionId if this location belongs to a section
     * - parent locationId if this is a nested sub-location
     * - null if this is a root-level location (no sections)
     */
    parentId: string | null;
    /** Direct child location IDs (maintained on register/unregister for O(1) lookup) */
    childIds: Set<string>;
    /**
     * What kind of location this is. Recorded here because a copy target's type has to travel with its id:
     * the duplicate mutation needs to know whether to duplicate into a room or an area, and the row that was
     * ticked is the only place that knows.
     */
    typeCategory: LocationTypeCategory;
};

type SectionEntry = {
    /** IDs of locations directly contained in this section */
    locationIds: Set<string>;
};

type SelectionState = {
    /** Whether selection mode is active */
    isActive: boolean;
    /** The location that triggered selection mode (cannot be deselected) */
    initialLocationId: string | null;
    /** Registered locations and their selection state */
    locations: Map<string, LocationEntry>;
    /** Registered sections (for grouping locations) */
    sections: Map<string, SectionEntry>;
    /**
     * Index of the location IDs whose entry is currently selected (mirrors `entry.isSelected === true`).
     * Kept in sync by every mutation.
     */
    selectedIds: Set<string>;
};

export const useLocationSelectionStore = defineStore('LocationSelection', {
    state: (): SelectionState => ({
        isActive: false,
        initialLocationId: null,
        locations: new Map(),
        sections: new Map(),
        selectedIds: new Set(),
    }),

    getters: {
        /**
         * Whether a row has registered itself yet. A row registers in `onMounted` but renders before that,
         * so between its first render and its mounted hook it legitimately has no entry here — the checkbox
         * getters throw for unregistered ids, so the cells check this first rather than swallowing the guard.
         */
        isLocationRegistered: (state) => (locationId: string) => state.locations.has(locationId),
        isSectionRegistered: (state) => (sectionId: string) => state.sections.has(sectionId),

        /**
         * Get the checkbox state (status and tooltip) for a location.
         */
        getLocationCheckbox:
            (state) =>
            (locationId: string): CheckboxState => {
                if (!state.isActive) {
                    return {
                        status: 'No-Selection',
                    };
                }

                const location = state.locations.get(locationId);
                if (!location) {
                    throw new Error(
                        `Location "${locationId}" not found in selection store. Is the location registered?`,
                    );
                }

                if (locationId === state.initialLocationId) {
                    return {
                        status: 'Disabled-Selected',
                        tooltip: getTooltipForStatus('Disabled-Selected'),
                    };
                }

                if (location.childIds.size > 0) {
                    const status = aggregateDescendantsStatus(state.locations, location.childIds);
                    return {
                        status,
                        tooltip: getTooltipForStatus(status),
                    };
                }

                if (location.isSelected) {
                    return {
                        status: 'Selected',
                        tooltip: getTooltipForStatus('Selected'),
                    };
                }
                return {
                    status: 'Not-Selected',
                    tooltip: getTooltipForStatus('Not-Selected'),
                };
            },

        /**
         * Get the checkbox state (status and tooltip) for a section.
         */
        getSectionCheckbox:
            (state) =>
            (sectionId: string): CheckboxState => {
                if (!state.isActive) {
                    return {
                        status: 'No-Selection',
                    };
                }

                const section = state.sections.get(sectionId);
                if (!section) {
                    throw new Error(`Section "${sectionId}" not found in selection store. Is the section registered?`);
                }

                if (section.locationIds.size === 0) {
                    return {
                        status: 'Disabled-Not-Selected',
                        tooltip: $t('section-empty-cannot-select-tooltip'),
                    };
                }

                // Section that contains only the initial (source) location: the source is selected and
                // there are no copy targets, so show it selected-but-disabled (can't be changed).
                if (
                    section.locationIds.size === 1 &&
                    state.initialLocationId &&
                    section.locationIds.has(state.initialLocationId)
                ) {
                    return {
                        status: 'Disabled-Selected',
                        tooltip: $t('section-only-initial-tooltip'),
                    };
                }

                // Count the initial (source) location too, so the section holding the source reflects it
                // as (partially) selected — consistent with hierarchy parent rows. Toggling the section
                // still can't deselect the source (see setLocationSelectionRecursive skipping the initial).
                const status = aggregateDescendantsStatus(state.locations, section.locationIds);
                return {
                    status,
                    tooltip: getTooltipForStatus(status),
                };
            },

        /**
         * The explanation to show when an expand/collapse control (a section OR a nested parent location)
         * must be disabled, or null when it may be toggled freely. Any ancestor of the location that
         * initiated selection mode (the source) must stay expanded so the source stays visible. Works for
         * both section ids and location ids: the source's ancestor chain runs through parent locations and
         * ends at the containing section id.
         */
        getExpandableDisabledExplanation:
            (state) =>
            (id: string): string | null => {
                if (!state.isActive || !state.initialLocationId) {
                    return null;
                }

                // Walk the source's ancestor chain, starting from the source's PARENT so the source's own
                // row is never locked. The walk ends at the containing section id (not in `locations`).
                let currentId: string | null = state.locations.get(state.initialLocationId)?.parentId ?? null;
                while (currentId) {
                    if (currentId === id) {
                        return state.sections.has(id)
                            ? $t('section-contains-initial-cannot-collapse-tooltip')
                            : $t('location-contains-initial-cannot-collapse-tooltip');
                    }
                    currentId = state.locations.get(currentId)?.parentId ?? null;
                }

                return null;
            },

        /**
         * Get the checkbox state for the entire timeline ("select all").
         */
        getTimelineCheckbox: (state): CheckboxState => {
            if (!state.isActive) {
                return {
                    status: 'No-Selection',
                };
            }

            if (state.locations.size === 0) {
                return {
                    status: 'Disabled-Not-Selected',
                    tooltip: getTimelineTooltipForStatus('Disabled-Empty'),
                };
            }

            // Only the initial (source) location exists (no copy targets): source is selected, disabled.
            if (state.locations.size === 1 && state.initialLocationId && state.locations.has(state.initialLocationId)) {
                return {
                    status: 'Disabled-Selected',
                    tooltip: getTimelineTooltipForStatus('Disabled-Only-Initial'),
                };
            }

            // Counts the initial (source) location too, so the site checkbox reflects the source as
            // selected — consistent with sections and hierarchy parent rows. The source can't be toggled off
            // (see toggleTimelineSelection).
            const status = aggregateDescendantsStatus(state.locations, new Set(state.locations.keys()));
            return {
                status,
                tooltip: getTimelineTooltipForStatus(status),
            };
        },

        /**
         * The copy targets, each with the type the duplicate mutation routes by. The type travels with the id
         * because the row that was ticked is the only place that knows it.
         */
        copyTargets: (state): CopyTargetLocation[] => {
            if (!state.isActive) {
                return [];
            }

            const targets: CopyTargetLocation[] = [];
            for (const locationId of state.selectedIds) {
                if (locationId === state.initialLocationId) {
                    continue;
                }

                const entry = state.locations.get(locationId);
                if (!entry) {
                    throw new Error(
                        `Location "${locationId}" not found in selection store. Is the location registered?`,
                    );
                }

                if (!isCopyTarget(entry)) {
                    continue;
                }

                targets.push({ id: locationId, typeCategory: entry.typeCategory });
            }
            return targets;
        },
    },

    actions: {
        /**
         * Register a section in the selection system.
         * Sections group locations and aggregate their selection state.
         */
        registerSection(sectionId: string) {
            if (this.sections.has(sectionId)) {
                return;
            }

            this.sections.set(sectionId, {
                locationIds: new Set(),
            });
        },

        /**
         * Unregister a section from the selection system.
         */
        unregisterSection(sectionId: string) {
            this.sections.delete(sectionId);
        },

        /**
         * Register a location in the selection system.
         * Should be called when a location row mounts.
         *
         * @param locationId - The location's unique ID
         * @param parentId - The sectionId or parent locationId, or null for root locations
         * @param typeCategory - Travels with the id: the duplicate mutation routes a copy target by it, and
         *   the row being registered is the only place that knows. No default, because a wrong value here is
         *   indistinguishable downstream from a real one.
         */
        registerLocation(locationId: string, parentId: string | null, typeCategory: LocationTypeCategory) {
            if (this.locations.has(locationId)) {
                return;
            }

            this.locations.set(locationId, {
                isSelected: false,
                parentId,
                childIds: new Set(),
                typeCategory,
            });

            // Link to parent (section or parent location)
            if (parentId) {
                const section = this.sections.get(parentId);
                if (section) {
                    section.locationIds.add(locationId);
                    return;
                }

                const parentLocation = this.locations.get(parentId);
                if (parentLocation) {
                    parentLocation.childIds.add(locationId);
                    return;
                }

                throw new Error(
                    `Parent "${parentId}" not found when registering location "${locationId}". Is the parent section or location registered?`,
                );
            }
        },

        /**
         * Unregister a location from the selection system.
         * Should be called when a location row unmounts.
         */
        unregisterLocation(locationId: string) {
            const location = this.locations.get(locationId);
            if (!location) {
                return;
            }

            // Remove from parent
            if (location.parentId) {
                const section = this.sections.get(location.parentId);
                if (section) {
                    section.locationIds.delete(locationId);
                } else {
                    const parentLocation = this.locations.get(location.parentId);
                    if (parentLocation) {
                        parentLocation.childIds.delete(locationId);
                    }
                }
            }

            this.locations.delete(locationId);
            this.selectedIds.delete(locationId);

            // A selection is anchored to its initiating location. Once that row is gone there is no popup
            // left to confirm or cancel through — a remounted allocation gets a fresh state machine with its
            // popup closed — so the checkboxes would stay on screen with no way to dismiss them.
            //
            // Collapsing a section keeps its first-column rows mounted (`v-show`) precisely so registration
            // survives, so this only fires when the row is really gone: switching the display mode, or the
            // location disappearing from the data.
            if (locationId === this.initialLocationId) {
                this.deactivateSelection();
            }
        },

        /**
         * Activate selection mode, starting from the given location.
         * The initial location will be selected and disabled.
         */
        activateSelection(initialLocationId: string) {
            const initialLocation = this.locations.get(initialLocationId);
            if (!initialLocation) {
                throw new Error(
                    `Initial location "${initialLocationId}" not found in selection store. Is the location registered?`,
                );
            }

            this.isActive = true;
            this.initialLocationId = initialLocationId;

            // Entries are unselected while selection is inactive (deactivate/discard clear them), so
            // activation only needs to mark the initial location selected.
            initialLocation.isSelected = true;
            this.selectedIds.add(initialLocationId);
        },

        /**
         * Deactivate selection mode and clear all selections.
         */
        deactivateSelection() {
            // No-op when already inactive; deactivation may be called more than once.
            if (!this.isActive) {
                return;
            }

            this.isActive = false;
            this.initialLocationId = null;

            // Reset only the currently-selected entries.
            for (const locationId of this.selectedIds) {
                const entry = this.locations.get(locationId);
                if (entry) {
                    entry.isSelected = false;
                }
            }
            this.selectedIds.clear();
        },

        /**
         * Discard changes and reset to initial state (only initial location selected).
         */
        discardSelectionChanges() {
            if (!this.isActive) {
                throw new Error(
                    'Discarding selection changes is not possible outside of active selection. This should never happen.',
                );
            }

            const initialLocationId = this.initialLocationId;
            if (!initialLocationId) {
                throw new Error(
                    'Cannot discard selection changes without an initial location. This should never happen.',
                );
            }

            // Reset every selected entry except the initial (which stays selected).
            for (const locationId of this.selectedIds) {
                if (locationId === initialLocationId) {
                    continue;
                }
                const entry = this.locations.get(locationId);
                if (entry) {
                    entry.isSelected = false;
                }
            }
            this.selectedIds.clear();
            this.selectedIds.add(initialLocationId);
        },

        /**
         * Toggle the selection state of a location.
         * If the location has child locations, toggles all descendants.
         */
        toggleLocationSelection(locationId: string) {
            if (!this.isActive) {
                throw new Error(
                    'Toggling the location is not possible outside of active selection. This should never happen.',
                );
            }

            if (locationId === this.initialLocationId) {
                throw new Error('Toggling the initial location is not possible. This should never happen.');
            }

            const location = this.locations.get(locationId);
            if (!location) {
                throw new Error(`Location "${locationId}" not found in selection store. Is the location registered?`);
            }

            if (location.childIds.size > 0) {
                // Parent location — determine target based on toggleable children only
                const aggregateStatus = aggregateDescendantsStatus(
                    this.locations,
                    location.childIds,
                    this.initialLocationId,
                );
                const newIsSelected = aggregateStatus !== 'Selected';
                this.setLocationSelectionRecursive(locationId, newIsSelected);
                return;
            }

            // Leaf location — simple toggle
            this.setEntrySelected(locationId, !location.isSelected);
        },

        /**
         * Toggle the selection state of all locations in a section.
         * Recursively affects nested sub-locations within each location.
         */
        toggleSectionSelection(sectionId: string) {
            if (!this.isActive) {
                throw new Error(
                    'Toggling the section is not possible outside of active selection. This should never happen.',
                );
            }

            const section = this.sections.get(sectionId);
            if (!section) {
                throw new Error(`Section "${sectionId}" not found in selection store. Is the section registered?`);
            }

            if (section.locationIds.size === 0) {
                throw new Error('Toggling an empty section is not possible. This should never happen.');
            }

            // Determine target state based on toggleable children only (excludes initial, includes nested)
            const aggregateStatus = aggregateDescendantsStatus(
                this.locations,
                section.locationIds,
                this.initialLocationId,
            );
            const newIsSelected = aggregateStatus !== 'Selected';

            for (const locationId of section.locationIds) {
                this.setLocationSelectionRecursive(locationId, newIsSelected);
            }
        },

        /**
         * Toggle the selection state of all locations in the timeline.
         */
        toggleTimelineSelection() {
            if (!this.isActive) {
                throw new Error(
                    'Toggling the timeline is not possible outside of active selection. This should never happen.',
                );
            }

            if (this.locations.size === 0) {
                throw new Error('Toggling an empty timeline is not possible. This should never happen.');
            }

            // Determine target: if any toggleable location is not selected, select all
            let hasUnselectedToggleable = false;
            for (const [locationId, entry] of this.locations) {
                if (locationId === this.initialLocationId || !isCopyTarget(entry)) {
                    continue;
                }
                if (!entry.isSelected) {
                    hasUnselectedToggleable = true;
                    break;
                }
            }

            const newIsSelected = hasUnselectedToggleable;

            for (const [locationId, entry] of this.locations) {
                if (locationId === this.initialLocationId || !isCopyTarget(entry)) {
                    continue;
                }
                this.setEntrySelected(locationId, newIsSelected);
            }
        },

        /**
         * Set selection state for a location and all its descendants.
         */
        setLocationSelectionRecursive(locationId: string, isSelected: boolean) {
            if (locationId === this.initialLocationId) {
                return;
            }

            const location = this.locations.get(locationId);
            if (!location) {
                throw new Error(
                    `Location "${locationId}" not found during recursive selection. This should never happen.`,
                );
            }

            this.setEntrySelected(locationId, isSelected);

            for (const childId of location.childIds) {
                this.setLocationSelectionRecursive(childId, isSelected);
            }
        },

        /**
         * Set a single entry's selection state, keeping the `selectedIds` index in sync.
         * No-op when the entry is missing or already in the requested state.
         */
        setEntrySelected(locationId: string, isSelected: boolean) {
            const location = this.locations.get(locationId);
            if (!location || location.isSelected === isSelected) {
                return;
            }

            location.isSelected = isSelected;
            if (isSelected) {
                this.selectedIds.add(locationId);
            } else {
                this.selectedIds.delete(locationId);
            }
        },
    },
});

// --- Helper functions ---

/**
 * Only rooms and areas hold actual allocations, so only their checkboxes carry a selection of their own.
 * Buildings, floors and zones show the state of the rooms and areas below them.
 */
function isCopyTarget(location: LocationEntry): boolean {
    return location.typeCategory === 'Room' || location.typeCategory === 'Area';
}

/**
 * Status values that can result from aggregation (never 'No-Selection' or disabled states).
 */
type AggregatedStatus = 'Selected' | 'Partially-Selected' | 'Not-Selected';

/**
 * Aggregate the selection status of every descendant (recursive).
 *
 * Two intentionally different uses:
 * - **Display** (`getLocationCheckbox`, `getSectionCheckbox`) omits `excludeId`, so the location that
 *   initiated selection counts as selected — a section holding the source reads as (partially) selected,
 *   consistent with hierarchy parent rows.
 * - **Toggle direction** (`toggleLocationSelection`, `toggleSectionSelection`) passes the initial location
 *   id, because the source can't be toggled and so must not influence which way the toggle goes.
 *
 * Only the display use changes behaviour today; the toggle callers pass the id so they stay correct if the
 * source ever stops being selected-while-active.
 *
 * @param excludeId - Location ID to skip, or null/omitted to count every descendant.
 */
function aggregateDescendantsStatus(
    locations: Map<string, LocationEntry>,
    childIds: Set<string>,
    excludeId?: string | null,
): AggregatedStatus {
    let hasSelected = false;
    let hasNotSelected = false;

    function traverse(ids: Set<string>) {
        for (const id of ids) {
            if (id === excludeId) {
                continue;
            }

            const location = locations.get(id);
            if (!location) {
                continue;
            }

            if (isCopyTarget(location)) {
                if (location.isSelected) {
                    hasSelected = true;
                } else {
                    hasNotSelected = true;
                }
            }

            // Early exit if we already know it's partial
            if (hasSelected && hasNotSelected) {
                return;
            }

            if (location.childIds.size > 0) {
                traverse(location.childIds);
                if (hasSelected && hasNotSelected) {
                    return;
                }
            }
        }
    }

    traverse(childIds);

    if (hasSelected && hasNotSelected) {
        return 'Partially-Selected';
    }
    if (hasSelected) {
        return 'Selected';
    }
    return 'Not-Selected';
}

/**
 * The statuses that reach this function: the three aggregated ones plus the initial location's.
 *
 * `Disabled-Not-Selected` is absent on purpose — both of its producers supply their own, more specific text.
 */
type LocationTooltipStatus = AggregatedStatus | 'Disabled-Selected';

function getTooltipForStatus(status: LocationTooltipStatus): string {
    switch (status) {
        case 'Selected':
            return $t('location-selected-tooltip');
        case 'Not-Selected':
            return $t('location-not-selected-tooltip');
        case 'Partially-Selected':
            return $t('location-partially-selected-tooltip');
        case 'Disabled-Selected':
            return $t('location-initial-selection-tooltip');
    }
}

/** The statuses the site-wide checkbox can reach. */
type TimelineTooltipStatus = AggregatedStatus | 'Disabled-Empty' | 'Disabled-Only-Initial';

/**
 * Timeline-specific tooltips (refers to "all locations" rather than a single location).
 */
function getTimelineTooltipForStatus(status: TimelineTooltipStatus): string {
    switch (status) {
        case 'Selected':
            return $t('timeline-selected-tooltip');
        case 'Not-Selected':
            return $t('timeline-not-selected-tooltip');
        case 'Partially-Selected':
            return $t('timeline-partially-selected-tooltip');
        case 'Disabled-Empty':
            return $t('timeline-empty-tooltip');
        case 'Disabled-Only-Initial':
            return $t('timeline-only-initial-tooltip');
    }
}
